import { rootApi } from './baseApi';
import { LiveDataResponse, SensorReading } from '../types/sensorData';
import { API_CONFIG } from '../config/api.config';

export const sensorDataApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getLiveData: builder.query<LiveDataResponse, { timeWindow?: number } | void>({
      query: (params) => {
        const queryParams: Record<string, string> = {};
        if (params && typeof params === 'object' && params.timeWindow) {
          queryParams.timeWindow = params.timeWindow.toString();
        }
        
        return {
          url: "/getLiveData",
          method: "GET",
          params: queryParams,
        };
      },
      providesTags: ['LiveData'],
      // Cache for 30 seconds to reduce API calls
      keepUnusedDataFor: 30,
      // Add stale time to prevent unnecessary refetches
      staleTime: 30000,
      transformResponse: (response: any[]) => {
        if (!response || !Array.isArray(response)) {
          console.warn('Invalid live data response:', response);
          return [];
        }
        return response.map(item => {
          const temp = parseFloat(item.temperature ?? '0');
          return {
            ...item,
            sensor_id: item.sensor_id?.toString() ?? '',
            sensor_name: item.sensor_name ?? '',
            temperature: temp === 8001 || temp === 8002 ? null : temp,
            temperature_2: item.temperature_2 !== undefined && item.temperature_2 !== null ? parseFloat(item.temperature_2) : undefined,
            humidity: item.humidity ? parseFloat(item.humidity) : undefined,
            RSSI: item.RSSI,
            SNR: item.SNR,
            Battery: item.Battery,
            timestamp: item.timestamp
          };
        });
      }
    }),

    getSensorReadings: builder.query<SensorReading[], { sensorId: number; startDate: string; endDate: string; channel?: 1 | 2; bucketMinutes?: number }>({
      query: ({ sensorId, startDate, endDate, channel, bucketMinutes }) => ({
        url: `/sensors/${sensorId}/readings`,
        method: "GET",
        params: {
          startDate,
          endDate,
          ...(channel != null ? { channel: String(channel) } : {}),
          ...(bucketMinutes != null ? { bucketMinutes: String(bucketMinutes) } : {}),
        }
      }),
      transformResponse: (response: any[]) => {
        if (!Array.isArray(response)) {
          console.warn('Invalid readings response:', response);
          return [];
        }
        return response.map(reading => {
          const rawTemp = reading.temperature;
          const temp = rawTemp != null ? parseFloat(rawTemp) : null;
          const rawMin = reading.temp_min;
          const rawMax = reading.temp_max;
          const temp2 = reading.temperature_2 != null ? parseFloat(reading.temperature_2) : undefined;
          return {
            timestamp: reading.timestamp,
            temperature: temp === null || temp === 8001 || temp === 8002 || (temp !== null && isNaN(temp)) ? null : temp,
            temp_min: rawMin != null ? parseFloat(rawMin) : null,
            temp_max: rawMax != null ? parseFloat(rawMax) : null,
            temperature_2: temp2,
            hum: reading.hum != null ? parseFloat(reading.hum) : undefined,
            battery: parseFloat(reading.Battery || '0'),
            rssi: reading.RSSI != null && reading.RSSI !== '' ? parseInt(String(reading.RSSI), 10) : null,
            snr: reading.SNR != null && reading.SNR !== '' ? parseInt(String(reading.SNR), 10) : null,
            sample_count: reading.sample_count ? parseInt(reading.sample_count) : 1,
          };
        });
      }
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetLiveDataQuery,
  useGetSensorReadingsQuery
} = sensorDataApi;